import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ExpandMoreIcon,
  FiberManualRecordIcon,
  Grid,
  Stack,
  Typography,
} from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import startCase from 'lodash/startCase';
import React from 'react';

import { ConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { IndicationStatus, OfferingType } from '../../../../../../../../graphql';
import { getInstrumentName } from '../../../../../../../offering-setup-next/utils';
import {
  UpdateHistory_CoveredIndicationVersionPartsFragment,
  UpdateHistory_IndicationVersionPartsFragment,
  UpdateHistory_MyIndicationVersionPartsFragment,
} from '../../graphql';
import InterestLevelGridRows from '../interest-level-grid/InterestLevelGridRows';
import useGetRevisionType, { RevisionType } from './hooks/useGetRevisionType';
import UpdatedAtText from './UpdatedAtText';
import UpdateDetailContentText from './UpdateDetailContentText';

export type Props = {
  offeringType: OfferingType | null;
  item:
    | UpdateHistory_IndicationVersionPartsFragment
    | UpdateHistory_CoveredIndicationVersionPartsFragment
    | UpdateHistory_MyIndicationVersionPartsFragment;
  pricingCurrencyCode?: string | null;
  handleOnRowExpand: (id: string) => void;
  expanded: boolean;
  securityTypeDisplayName: string | null;
  issuerName: string | null;
  convertibleTalkRanges: ConvertibleFilingTalkRanges;
  isIndicationPriceAsPercentageParEnabled: boolean | null;
};

const getStatusColor = (status: IndicationStatus) => {
  switch (status) {
    case IndicationStatus.Active:
      return 'success';
    case IndicationStatus.Cancelled:
      return 'error';
    default:
      return 'disabled';
  }
};

const IndicationVersionDetail: React.FC<Props> = ({
  offeringType,
  item,
  handleOnRowExpand,
  expanded,
  securityTypeDisplayName,
  issuerName,
  convertibleTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
}) => {
  const intl = useIntl();
  const revisionType = useGetRevisionType(item);
  const isUpdate = revisionType === RevisionType.Updated;

  const tranche = item?.tranche;
  const deliveryInstrument = tranche?.deliveryInstruments.find(
    instrument => instrument.id === item.instrumentId
  );
  const currency = defaultTo(
    tranche?.demandCurrencies?.find(demandCurrency => demandCurrency === item.currencyCode),
    item.currencyCode
  )?.toUpperCase();

  const isSellSideIndicationVersion =
    item?.__typename === 'InstitutionalIndicationVersion' ||
    item?.__typename === 'CoveredInstitutionalIndicationVersion';

  const investorName = isSellSideIndicationVersion
    ? item?.investorInformation?.bankInvestorName || item?.investorInformation?.cmgEntityName
    : 'Your Firm';

  const modifiedByFirmName = item?.isBuySideRevision
    ? investorName
    : defaultTo(item?.auditInfo.modifiedByFirmName, '0');

  const updatedAt = intl.formatTime(item.auditInfo.modifiedAt, { format: 'dateTime' });

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      sx={{ boxShadow: 'none' }}
      data-testid="update-history-indication-version-detail-row"
    >
      <AccordionSummary
        expandIcon={isUpdate ? <ExpandMoreIcon onClick={() => handleOnRowExpand(item.id)} /> : ''}
      >
        <Stack sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <UpdatedAtText updatedAt={updatedAt} updatedBy={modifiedByFirmName} />
            </Stack>
            <Stack direction="row" sx={{ flex: '0 0 60px', alignItems: 'center' }}>
              <FiberManualRecordIcon
                sx={{ fontSize: '12px', marginRight: '6px' }}
                color={getStatusColor(item.status)}
              />
              {startCase(item?.status?.toLowerCase())}
            </Stack>
          </Stack>
          {!isUpdate && (
            <UpdateDetailContentText
              revisionType={revisionType}
              investorName={investorName}
              isSellSideIndicationVersion={isSellSideIndicationVersion}
            />
          )}
          {isUpdate && (
            <Box
              sx={{
                marginTop: theme => theme.spacing(2),
                padding: theme => `0 ${theme.spacing(2)}`,
              }}
            >
              <Grid container rowSpacing={1} columnSpacing={3}>
                <Grid item xs={4}>
                  <Typography color="gray"> Quantity </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography color="gray"> Unit </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography color="gray"> Order </Typography>
                </Grid>
                <InterestLevelGridRows
                  offeringType={offeringType}
                  item={item}
                  currency={currency!}
                  convertibleTalkRanges={convertibleTalkRanges}
                  isIndicationPriceAsPercentageParEnabled={isIndicationPriceAsPercentageParEnabled}
                />
              </Grid>
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      {isUpdate && (
        <AccordionDetails sx={{ paddingTop: '0px' }}>
          <Box sx={{ paddingRight: '40px', paddingLeft: theme => theme.spacing(2) }}>
            <Grid container rowSpacing={1} columnSpacing={3} data-testid="tranche-grid">
              <Grid item xs={4}>
                <Typography color="gray"> Tranche </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography color="gray"> Currency </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography color="gray"> Instrument </Typography>
              </Grid>
              <Grid item xs={4}>
                {item.tranche?.name}
              </Grid>
              <Grid item xs={4}>
                {currency}
              </Grid>
              <Grid item xs={4}>
                {deliveryInstrument &&
                  defaultTo(
                    deliveryInstrument.name,
                    getInstrumentName({
                      issuerName,
                      securityTypeDisplayName: defaultTo(securityTypeDisplayName, null),
                      stockSymbol: defaultTo(deliveryInstrument.stockSymbol, null),
                      isDepositaryReceipt: !!deliveryInstrument.isDepositaryReceipt,
                    })
                  )}
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default IndicationVersionDetail;
